import { CommonModule } from "@angular/common";
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  AnnouncementInboxItem,
  announcementText,
} from "../../services/announcement/announcement.models";
import { AnnouncementClientApiService } from "../../services/announcement/announcement-client-api.service";
import { AnnouncementOverlayService } from "./announcement-overlay.service";
import { AnnouncementAudienceService } from "../../services/announcement/announcement-audience.service";

/**
 * Header "what's new" icon — lets a user re-open an announcement after it's been seen/dismissed
 * ("still visible in top header to re see it or not"). Self-contained (own styling, own fetch) so
 * it drops into header.component.html without needing to touch that file's large existing
 * TS/SCSS. Badge only counts announcements with showNotificationBadge=true.
 *
 * The announcement itself is displayed by the launcher at the app-frame root, not from here —
 * see AnnouncementOverlayService.
 */
@Component({
  selector: "app-announcement-inbox-button",
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
  templateUrl: "./announcement-inbox-button.component.html",
  styleUrl: "./announcement-inbox-button.component.scss",
})
export class AnnouncementInboxButtonComponent implements OnInit, OnDestroy {
  @Input() labelled = false;
  items: AnnouncementInboxItem[] = [];
  open = false;

  private closedSub?: Subscription;

  constructor(
    private announcementClientApi: AnnouncementClientApiService,
    private translateService: TranslateService,
    private overlay: AnnouncementOverlayService,
    private hostRef: ElementRef<HTMLElement>,
    private audience: AnnouncementAudienceService,
  ) {}

  get unreadCount(): number {
    return this.items.filter(
      (i) => !i.seen && i.announcement.showNotificationBadge,
    ).length;
  }

  get lang(): string {
    return this.translateService.currentLang || "en";
  }

  text(map: Record<string, string> | undefined | null): string {
    return announcementText(map, this.lang);
  }

  ngOnInit(): void {
    // Re-reading the inbox on close is what turns the unread dot off once an announcement
    // has been re-opened — the launcher owns the overlay, so the signal comes from there.
    this.closedSub = this.overlay.closed$.subscribe(() => this.loadInbox());
    this.loadInbox();
  }

  ngOnDestroy(): void {
    this.closedSub?.unsubscribe();
  }

  private loadInbox(): void {
    this.audience.isNewCustomer().subscribe((isNewCustomer) => {
      if (isNewCustomer) {
        this.items = [];
        this.open = false;
        return;
      }
      this.announcementClientApi.inbox().subscribe({
        next: (res) => {
          this.items = res.body ?? [];
        },
        error: () => {
          this.items = [];
        },
      });
    });
  }

  toggle(): void {
    this.open = !this.open;
  }

  openItem(item: AnnouncementInboxItem): void {
    this.overlay.open(item.announcement);
    this.open = false;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.open &&
      !this.hostRef.nativeElement.contains(event.target as Node)
    ) {
      this.open = false;
    }
  }
}
